import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-loneliness",
  title: "A prayer for loneliness — on a quiet evening",
  h1: "A prayer for loneliness",
  description:
    "A prayer for an evening with nobody in it, four passages that take being alone seriously, and why the phone makes it worse rather than better.",
  targets: [
    "prayer for loneliness",
    "prayer when you feel alone",
    "prayer for a lonely night",
    "prayer for friendship",
  ],
  published: "2026-09-03",
  standfirst:
    "The first thing the Bible calls not good is a person on their own. It is not a small problem and it is not solved by being told God is enough.",
  takeaways: [
    "Genesis 2:18 says being alone is not good before anything has gone wrong. Loneliness is not evidence of a fault in you.",
    "Psalm 68:6 says God sets the lonely in families — the answer offered is other people, and it is worth noticing that it is not offered as a feeling.",
    "The phone is the worst available response. It supplies the appearance of contact at exactly the strength needed to postpone the real thing.",
  ],
  when: [
    "On the third evening in a row with nobody in the house.",
    "After a day of messages and meetings in which nobody asked you anything.",
    "In a new city, a new job, a new church, in month two.",
    "When you have been scrolling for an hour and feel further away than when you started.",
  ],
  prayer: [
    "God, I am on my own again tonight, and I have run out of ways to make that sound fine.",
    "You said it is not good for a person to be alone before anything had even gone wrong, so I am not going to apologise for minding.",
    "Send me someone. And in the meantime give me the nerve to be the one who asks first, which is the part I keep avoiding because it might not land.",
    "Where I have made this worse — where I have said no to things because saying yes is exhausting — show me that without making a speech about it.",
    "Sit with me tonight. Not instead of people. Until there are some.",
  ],
  short:
    "God, I am on my own again. Send me someone, and give me the nerve to ask first.",
  honest: [
    "This prayer does not claim God is a substitute for company. Genesis says being alone is not good while the man is in an unspoiled garden with unbroken access to God, which rules that reading out fairly decisively.",
    "It also cannot manufacture a friendship. The passages point at other people, and other people have to be approached, repeatedly, in ways that are awkward and occasionally rebuffed.",
    "And loneliness that has become numbness — weeks of not wanting to see anybody rather than wanting to and not being able — is closer to depression than to solitude, and is worth taking to a doctor.",
  ],
  verses: [
    {
      verse: {
        text: 'Yahweh God said, "It is not good for the man to be alone. I will make him a helper comparable to him."',
        ref: "Genesis 2:18",
      },
      note: "The first not good in the Bible, and its position is the argument: nothing has gone wrong yet, the garden is intact, and the man is not short of access to God. Being on your own is diagnosed as a deficiency in a world where everything else is working, which means loneliness is not evidence that something is wrong with you.",
    },
    {
      verse: {
        text: "God sets the lonely in families. He brings out the prisoners with singing, but the rebellious dwell in a sun-scorched land.",
        ref: "Psalm 68:6",
      },
      note: "The remedy named is concrete and social: families, plural, and the surrounding verses are about the fatherless and widows being given a place. Nothing here offers a feeling of divine company as the answer to isolation. It offers households, which is a considerably more demanding promise and a considerably more useful one.",
    },
    {
      verse: {
        text: "Two are better than one, because they have a good reward for their labor. For if they fall, the one will lift up his fellow; but woe to him who is alone when he falls, and doesn't have another to lift him up.",
        ref: "Ecclesiastes 4:9–10",
      },
      note: "Ecclesiastes is the book least inclined to comfort anybody, which makes this unusually persuasive. The case is entirely practical — two people can do more, and one of them can pick the other up — and the woe is not moral disapproval but a plain observation about what happens to a person who falls with nobody there.",
    },
    {
      verse: {
        text: "Turn to me, and have mercy on me, for I am desolate and afflicted.",
        ref: "Psalm 25:16",
      },
      note: "Desolate here is a word for being solitary, an only one. The psalm asks for God to turn towards him and does not pretend that settles the loneliness — the next verses go straight on to trouble, distress and enemies. It is a prayer that says the thing without resolving it, which is often what a Tuesday evening actually needs.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "It is not a spiritual failure",
      id: "it-is-not-a-spiritual-failure",
    },
    {
      type: "p",
      text: "A lot of Christian advice about loneliness implies that a person close enough to God would not feel it. Genesis rules that out on the second page: the man is alone in a perfect world with nothing between him and God, and the verdict is not good.",
    },
    {
      type: "p",
      text: "Which means the honest prayer is not for the feeling to stop. It is for people, and for the courage to go and find them.",
    },
    {
      type: "h2",
      text: "The phone is the wrong answer",
      id: "the-phone-is-the-wrong-answer",
    },
    {
      type: "p",
      text: "Loneliness produces an urge for contact, and {the nearest available thing} supplies contact at about ten per cent strength with no effort required. That is precisely the problem: it is enough to take the edge off, and taking the edge off is what stops you doing the hard thing.",
      links: {
        "the nearest available thing": "/blog/dopamine-detox-christian",
      },
    },
    {
      type: "p",
      text: "An hour of scrolling almost always leaves a lonely person lonelier, and the mechanism is not mysterious. You have spent an hour watching people be together.",
    },
    {
      type: "h2",
      text: "Ask first",
      id: "ask-first",
    },
    {
      type: "p",
      text: "Almost everybody waiting to be invited is waiting alongside somebody else waiting to be invited. The asymmetry is that asking costs something and being asked does not, so the queue never moves.",
    },
    {
      type: "note",
      text: "The specific and slightly humiliating advice that works: ask three people, expect two to say no, and do it again next month. It is a numbers problem far more than it is a likeability problem.",
    },
  ],
  faq: [
    {
      q: "What can I pray when I feel lonely?",
      a: '"God, I am on my own again. Send me someone, and give me the nerve to ask first." The second half is the part that changes anything.',
    },
    {
      q: "Does the Bible say anything about being lonely?",
      a: "The first thing called not good in Genesis is a person being alone — before anything has gone wrong, in a world where God is fully present. Psalm 68:6 offers families rather than feelings as the answer.",
    },
    {
      q: "Why does social media make loneliness worse?",
      a: "Because it supplies contact at about ten per cent strength for no effort, which is exactly enough to take the edge off and stop you doing the harder thing. An hour of it is an hour watching other people be together.",
    },
  ],
  relatedPrayers: ["prayer-for-a-friend", "prayer-for-comparison"],
  related: ["dopamine-detox-christian", "social-media-fast-guide"],
  relatedTopics: [
    "bible-verses-about-comparison",
    "bible-verses-about-grief",
    "bible-verses-about-friendship",
  ],
  relatedPassages: ["psalm-23", "isaiah-41-10"],
};
